import type { IconName } from "@dms/ui";

export const DOC_FLOW_ADMIN_ROLES = ["DOC_FLOW_ADMIN"] as const;
export const SYSTEM_ADMIN_ROLES = ["SYSTEM_ADMIN"] as const;
export const ADMIN_ROLES = ["DOC_FLOW_ADMIN", "SYSTEM_ADMIN"] as const;
// ADR-044 D6: аудит-журнал доступний і AUDITOR (право audit:read було без UI).
export const AUDIT_ROLES = ["SYSTEM_ADMIN", "AUDITOR"] as const;

export interface NavItem {
  label: string;
  path: string;
  icon: IconName;
  requiredRoles?: readonly string[];
}

export interface NavSection {
  heading?: string;
  items: NavItem[];
}

// Admin SPA sidebar (ClearFlow shell): згруповано за призначенням.
export const NAV_SECTIONS: NavSection[] = [
  {
    heading: "Документообіг",
    items: [
      { label: "Типи документів", path: "/admin/doc-types", icon: "docs", requiredRoles: DOC_FLOW_ADMIN_ROLES },
      { label: "Типи задач", path: "/admin/task-types", icon: "tag", requiredRoles: DOC_FLOW_ADMIN_ROLES },
      { label: "Маршрути", path: "/admin/routes", icon: "route", requiredRoles: DOC_FLOW_ADMIN_ROLES },
      { label: "Шаблони процесів", path: "/admin/process-templates", icon: "layers", requiredRoles: ADMIN_ROLES },
      { label: "LLM-роботи", path: "/admin/robots", icon: "send", requiredRoles: ADMIN_ROLES },
      { label: "Групи виконавців", path: "/admin/groups", icon: "user" },
      // TD-0060: «Області видимості» (/admin/scopes) приховано до релізу екрана —
      // не рекламуємо nav-пункт, що веде у PlaceholderPage. Повернути разом з UI.
    ],
  },
  {
    heading: "Довідники",
    items: [
      { label: "Довідники", path: "/admin/dictionaries", icon: "book", requiredRoles: DOC_FLOW_ADMIN_ROLES },
      { label: "Лічильники", path: "/admin/counters", icon: "tag", requiredRoles: DOC_FLOW_ADMIN_ROLES },
      { label: "Виробничий календар", path: "/admin/work-calendar", icon: "calendar", requiredRoles: DOC_FLOW_ADMIN_ROLES },
    ],
  },
  {
    heading: "Доступ",
    items: [
      {
        label: "Користувачі",
        path: "/admin/users",
        icon: "user",
        requiredRoles: SYSTEM_ADMIN_ROLES,
      },
      {
        label: "Підрозділи",
        path: "/admin/departments",
        icon: "fund",
        requiredRoles: SYSTEM_ADMIN_ROLES,
      },
      { label: "Ролі", path: "/admin/roles", icon: "flag", requiredRoles: SYSTEM_ADMIN_ROLES },
    ],
  },
  {
    heading: "Система",
    items: [
      { label: "Обробники", path: "/admin/handlers", icon: "send", requiredRoles: ADMIN_ROLES },
      { label: "Зовнішні системи", path: "/admin/external-systems", icon: "externalLink", requiredRoles: SYSTEM_ADMIN_ROLES },
      { label: "Email-шаблони", path: "/admin/email-templates", icon: "mail", requiredRoles: ADMIN_ROLES },
      { label: "Довідка", path: "/admin/help", icon: "book", requiredRoles: ADMIN_ROLES },
      { label: "Системні налаштування", path: "/admin/settings", icon: "settings", requiredRoles: ADMIN_ROLES },
      { label: "Аудит", path: "/admin/audit", icon: "audit", requiredRoles: AUDIT_ROLES },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

export function navSectionsForRoles(roles: readonly string[]): NavSection[] {
  const grantedRoles = new Set(roles);
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        item.requiredRoles === undefined ||
        item.requiredRoles.some((role) => grantedRoles.has(role)),
    ),
  })).filter((section) => section.items.length > 0);
}
